import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { MENTION_SEARCH_HIGHLIGHT_SPEC_KEY, } from './mention-search-highlight-node-view.js';
import { getMentionSearchText, isSearchableMentionNode } from './mention-search-text.js';
export const searchHighlightKey = new PluginKey('narrativebenchSearchHighlight');
export const buildSearchMatchDecorations = (doc, matches, activeIndex) => {
    if (matches.length === 0) {
        return DecorationSet.empty;
    }
    const decorations = [];
    matches.forEach((match, index) => {
        const className = index === activeIndex ? 'ProseMirror-active-search-match' : 'ProseMirror-search-match';
        const plainFrom = match.plainFrom;
        const plainTo = match.plainTo;
        if (plainFrom >= plainTo) {
            return;
        }
        let plainIndex = 0;
        doc.descendants((node, pos) => {
            if (node.isText && node.text) {
                const nodePlainFrom = plainIndex;
                const nodePlainTo = plainIndex + node.text.length;
                const overlapFrom = Math.max(nodePlainFrom, plainFrom);
                const overlapTo = Math.min(nodePlainTo, plainTo);
                if (overlapFrom < overlapTo) {
                    decorations.push(Decoration.inline(pos + (overlapFrom - nodePlainFrom), pos + (overlapTo - nodePlainFrom), { class: className }));
                }
                plainIndex = nodePlainTo;
                return;
            }
            if (isSearchableMentionNode(node)) {
                const mentionText = getMentionSearchText(node);
                const nodePlainFrom = plainIndex;
                const nodePlainTo = plainIndex + mentionText.length;
                const overlapFrom = Math.max(nodePlainFrom, plainFrom);
                const overlapTo = Math.min(nodePlainTo, plainTo);
                if (overlapFrom < overlapTo) {
                    decorations.push(Decoration.node(pos, pos + node.nodeSize, {}, {
                        [MENTION_SEARCH_HIGHLIGHT_SPEC_KEY]: {
                            className,
                            textFrom: overlapFrom - nodePlainFrom,
                            textTo: overlapTo - nodePlainFrom,
                        },
                    }));
                }
                plainIndex = nodePlainTo;
            }
        });
    });
    return DecorationSet.create(doc, decorations);
};
const mapMatches = (matches, tr) => {
    if (!tr.docChanged || matches.length === 0) {
        return matches;
    }
    const mapped = [];
    for (const match of matches) {
        const from = tr.mapping.map(match.from, 1);
        const to = tr.mapping.map(match.to, -1);
        if (from < to) {
            mapped.push({
                from,
                to,
                plainFrom: match.plainFrom,
                plainTo: match.plainTo,
            });
        }
    }
    return mapped;
};
export const setSearchMatchHighlights = (tr, matches, activeIndex = -1) => tr.setMeta(searchHighlightKey, { matches, activeIndex });
export const clearSearchMatchHighlights = (tr) => setSearchMatchHighlights(tr, [], -1);
export const SearchHighlightExtension = Extension.create({
    name: 'searchHighlight',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                key: searchHighlightKey,
                state: {
                    init() {
                        return {
                            matches: [],
                            activeIndex: -1,
                            deco: DecorationSet.empty,
                        };
                    },
                    apply(tr, prev, _oldState, state) {
                        const meta = tr.getMeta(searchHighlightKey);
                        if (meta) {
                            return {
                                matches: meta.matches,
                                activeIndex: meta.activeIndex,
                                deco: buildSearchMatchDecorations(state.doc, meta.matches, meta.activeIndex),
                            };
                        }
                        if (!tr.docChanged && !tr.selectionSet) {
                            return prev;
                        }
                        const matches = mapMatches(prev.matches, tr);
                        const activeIndex = prev.activeIndex >= 0 && prev.activeIndex < matches.length
                            ? prev.activeIndex
                            : -1;
                        return {
                            matches,
                            activeIndex,
                            deco: buildSearchMatchDecorations(state.doc, matches, activeIndex),
                        };
                    },
                },
                props: {
                    decorations(state) {
                        return searchHighlightKey.getState(state)?.deco ?? DecorationSet.empty;
                    },
                },
            }),
        ];
    },
});
//# sourceMappingURL=search-highlight.js.map